import { useQuery } from '@tanstack/react-query';
import { LocaleLink as Link } from '../i18n/locale-link';
import { useT } from '../i18n/locale-context';
import { ArrowRight } from './icons';
import { fetchPlans } from '../lib/billing-client';
import { useAuth } from '../lib/auth-context';
import { isPaidUser } from '../lib/user-display';
import { track } from '../lib/funnel';

/**
 * The entry price, grouped in so'm: 79_000 → "79 000".
 *
 * Deliberately NOT the old compact "79K / 2.4M" notation. That form existed to
 * fit five prices in a row, and it made them harder to read than the thing it
 * was compressing — «2.4M сум» is not how anyone says or writes a price here.
 * One price has room to be written properly.
 */
function fmtSom(uzs: number): string {
  return uzs.toLocaleString('ru-RU').replace(/,/g, ' ');
}

/**
 * m16 inline upgrade banner: a live teaser of the paid plans, priced in so'm.
 *
 * Data-driven off the SAME `fetchPlans` source as the /plans page
 * (`PlansService.listActive` → `OFFERED_KEYS`), so the teaser can never drift from
 * the Тарифы page again (it previously hard-coded a superseded ИП/Бизнес V2 model).
 * Shows the paid tiers only — Free is omitted. On prerendered pages the chips
 * hydrate in once the query resolves; SSR and initial client state both start
 * empty, so there is no hydration mismatch.
 */
export function UpgradeBanner() {
  const t = useT();
  const { user } = useAuth();
  const { data: plans } = useQuery({ queryKey: ['plans'], queryFn: fetchPlans });
  // ИП is gone: desktop-only on /plans from 2026-09-06, withdrawn from the grid
  // entirely on 2026-09-19 (LTV 148k against a CAC of 494k). This banner shows on
  // every catalogue page, so quoting «от 79 000» advertised a tier the tap could
  // not reach even then, and now could not reach at all. Pro is the entry point
  // the page actually sells. The filter predates the withdrawal and still stands
  // on its own — /plans could re-offer ИП and this banner would still lead with Pro.
  const paid = (plans ?? []).filter((p) => p.priceUzs > 0 && !p.key.startsWith('ip'));
  // The entry point, taken from the data rather than named — repricing a plan
  // or introducing a cheaper one must not leave this teaser quoting a stale
  // figure, which is precisely what a hard-coded price would do.
  const cheapest = paid.reduce<(typeof paid)[number] | null>(
    (min, p) => (min === null || p.priceUzs < min.priceUzs ? p : min),
    null,
  );
  // The sub-line: the year pitch when the live prices back a real ×2 (12 ×
  // monthly ≥ 1.9 × annual, the same threshold the /plans toggle uses), else
  // the plain «Pro и Бизнес · месяц или год».
  const monthly = paid.find((p) => p.key === 'pro-monthly');
  const annual = paid.find((p) => p.key === 'pro-yearly');
  const yearDeal = !!monthly && !!annual && (monthly.priceUzs * 12) / annual.priceUzs >= 1.9;

  // Never pitch Pro to someone who already pays for it. Anonymous / free viewers
  // (and SSR, where there's no user yet) still see it; a Pro session hides it on
  // hydration once auth resolves — same deferred-populate pattern as the price
  // chips above, so no hydration mismatch.
  if (isPaidUser(user)) return null;

  return (
    /* No outer margin. Spacing belongs to whatever lays this out — carrying
       `mt-5` meant that inside the settings page's `gap-2` section it stacked
       with the gap and left a 28px hole under the heading. The three public
       pages that relied on it now say so themselves. */
    <div className="flex flex-col items-start justify-between gap-5 overflow-hidden rounded-lg border border-edge bg-bone-surface px-6 py-[18px] shadow-m-sm lg:flex-row lg:items-center">
      {/* No accent rail. A coloured strip down the left edge of a rounded card
          is decoration that encodes nothing — it marked this card as important
          without saying why, and every card wearing one cancels the others out.
          The eyebrow below already names what this is. */}
      <div>
        <div className="mb-1 text-[10.5px] font-bold uppercase tracking-[0.08em] text-brand">
          {t('plans.eyebrow')}
        </div>
        <div className="mb-1 text-[15px] font-bold text-ink">{t('plans.title')}</div>
        <div className="text-[13px] text-ink-muted">{t('plans.subtitle')}</div>
      </div>
      <div className="flex w-full flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6 lg:w-auto lg:shrink-0">
        {/* ONE price — the cheapest — not the whole matrix.
            Five prices in a row could not be compared: they mix monthly and
            annual across three tiers, so «399K / 2.4M» invites a reading where
            one is six times the other rather than the same plan billed twice.
            At 375px they wrapped into a ragged 3-then-2 grid. A teaser's job is
            to establish the entry point and hand off; the /plans page is where
            tiers are actually compared, and it has the room to do it. */}
        {cheapest && (
          <div className="text-center sm:text-left">
            <div className="font-brand text-[19px] font-extrabold leading-[1.1] text-ink">
              <span className="text-[13px] font-bold text-ink-muted">{t('plans.name.pro')} — </span>
              {fmtSom(cheapest.priceUzs)}{' '}
              <span className="text-[12px] font-bold text-ink-muted">
                {t('plans.som')}
                {cheapest.billingPeriod === 'monthly' ? ` ${t('plans.perMonth')}` : ''}
              </span>
            </div>
            <div className="mt-0.5 text-[11px] font-medium text-ink-muted">
              {yearDeal ? t('plans.bannerYear') : t('plans.bannerPair')}
            </div>
          </div>
        )}
        <Link
          to="/plans"
          onClick={() => track('upgrade_cta_clicked', { source: 'banner' })}
          className="inline-flex h-11 w-full items-center justify-center gap-1.5 rounded-md bg-brand px-[18px] text-[13px] font-bold text-white transition-colors hover:bg-brand-mid sm:h-[38px] sm:w-auto lg:shrink-0"
        >
          {t('plans.cta')}
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </div>
  );
}
